/**
 * Static Scene Display Component
 *
 * Simplified scene viewer for gallery display.
 * Shows scenes instantly without parallax delays or admin features.
 * Read-only display optimized for fast loading.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ClothedEyeLoader from './ClothedEyeLoader';
import './StaticSceneDisplay.css';

const StaticSceneDisplay = ({
  sceneStickers = [],
  height = '60vh',
  backgroundColor = '#1a1a1a',
  scaleToFit = false,
}) => {
  const [loadedStickers, setLoadedStickers] = useState([]);
  const [allLoaded, setAllLoaded] = useState(false);

  // Load sticker images immediately (no delay)
  useEffect(() => {
    if (!sceneStickers || sceneStickers.length === 0) {
      setLoadedStickers([]);
      setAllLoaded(true);
      return;
    }

    // ONLY process stickers that have svgPath
    const validStickers = sceneStickers.filter((sticker) => sticker.svgPath);

    if (validStickers.length === 0) {
      console.log('⚠️ No stickers with svgPath found');
      setLoadedStickers([]);
      setAllLoaded(true);
      return;
    }

    // Process valid stickers
    const processed = validStickers.map((sticker) => ({
      ...sticker,
      loaded: false,
      error: false,
    }));

    setLoadedStickers(processed);
    setAllLoaded(false);
  }, [sceneStickers]);

  // Check if all loaded
  useEffect(() => {
    if (loadedStickers.length === 0) return;
    const allDone = loadedStickers.every((s) => s.loaded || s.error);
    if (allDone) {
      setAllLoaded(true);
    }
  }, [loadedStickers]);

  // Force hide loader after 2 seconds even if images are still loading
  useEffect(() => {
    if (loadedStickers.length === 0) return;
    const timeout = setTimeout(() => {
      setAllLoaded(true);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [loadedStickers]);

  // Handle image load success
  const handleImageLoad = (index) => {
    setLoadedStickers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], loaded: true };
      return updated;
    });
  };

  // Handle image load error
  const handleImageError = (index) => {
    setLoadedStickers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], error: true };
      return updated;
    });
  };

  // Calculate scene bounds
  const getSceneBounds = () => {
    if (!sceneStickers || sceneStickers.length === 0) {
      return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    sceneStickers.forEach((sticker) => {
      const x = sticker.xOffset || 0;
      const y = sticker.yOffset || 0;
      // Estimate sticker size (most are around 150-250px)
      const estimatedSize = 200;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + estimatedSize);
      maxY = Math.max(maxY, y + estimatedSize);
    });

    return { minX, minY, maxX, maxY };
  };

  // Calculate scale and offset for scaleToFit
  const getTransform = () => {
    if (!scaleToFit) {
      return { transform: 'none' };
    }

    const bounds = getSceneBounds();
    const sceneWidth = bounds.maxX - bounds.minX;
    const sceneHeight = bounds.maxY - bounds.minY;

    // Container dimensions (rough estimates for gallery cards)
    const containerWidth = 340; // Gallery card width
    const containerHeight = parseInt(height) || 240;

    // Calculate scale to fit
    const scaleX = (containerWidth * 0.9) / sceneWidth;
    const scaleY = (containerHeight * 0.9) / sceneHeight;
    const scale = Math.min(scaleX, scaleY, 1);

    // Center the scene
    const offsetX = (containerWidth - sceneWidth * scale) / 2 - bounds.minX * scale;
    const offsetY = (containerHeight - sceneHeight * scale) / 2 - bounds.minY * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: '0 0',
    };
  };

  if (loadedStickers.length === 0) {
    return (
      <div
        className="static-scene-display"
        style={{
          height,
          backgroundColor,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <div className="scene-empty-state">
          <p>No stickers in this scene</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="static-scene-display"
      style={{
        height,
        backgroundColor: '#2a2a2a', // Medium gray so black stickers are visible
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* Container for all stickers */}
      <div
        className="stickers-container"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          ...getTransform(),
        }}
      >
        {loadedStickers.map((sticker, index) => {
          if (!sticker.svgPath) {
            return null;
          }

          return (
            <img
              key={`${sticker.listingId}-${sticker.color}-${index}`}
              src={sticker.svgPath}
              alt={sticker.name || sticker.title || 'Sticker'}
              style={{
                position: 'absolute',
                left: `${sticker.xOffset || 0}px`,
                top: `${sticker.yOffset || 0}px`,
                opacity: 1,
                pointerEvents: 'none',
                userSelect: 'none',
                maxWidth: '200px',
                height: 'auto',
                transform: sticker.isFlipped ? 'scaleX(-1)' : 'none',
              }}
              onLoad={() => handleImageLoad(index)}
              onError={() => handleImageError(index)}
              draggable={false}
            />
          );
        })}
      </div>

      {/* Loading indicator */}
      {!allLoaded && (
        <div className="scene-loading-indicator">
          <ClothedEyeLoader size="small" showLogo={false} showTagline={false} />
        </div>
      )}
    </div>
  );
};

StaticSceneDisplay.propTypes = {
  sceneStickers: PropTypes.arrayOf(
    PropTypes.shape({
      listingId: PropTypes.string,
      svgPath: PropTypes.string,
      xOffset: PropTypes.number,
      yOffset: PropTypes.number,
      name: PropTypes.string,
      title: PropTypes.string,
      isFlipped: PropTypes.bool,
    })
  ),
  height: PropTypes.string,
  backgroundColor: PropTypes.string,
  scaleToFit: PropTypes.bool,
};

export default StaticSceneDisplay;
